/**
 * TimeSlotFilters - Filter controls for bay selection and duration
 * Provides dropdown selectors for bay and session duration
 */

import { HourglassIcon, LocationIcon } from '@butter/ui/icons';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@butter/ui/primitives/inputs';
import { formatDuration } from './utils';
import type { TimeSlotFiltersProps } from './types';

const TRIGGER_CLASS_NAMES =
  'h-auto w-auto gap-2 border-0 bg-transparent p-0 pl-1 pr-1 text-sm font-semibold text-fg-secondary shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-theme-600 focus-visible:ring-offset-2';

export function TimeSlotFilters({
  durationDisplay = 'select',
  bays,
  selectedBayId,
  durationOptions,
  availableDurations,
  selectedDuration,
  onBayChange,
  onDurationChange,
  layout = 'inline',
  showBayFilter = true,
  formatOptionLabel = formatDuration,
  formatTriggerLabel,
}: TimeSlotFiltersProps) {
  // Lengths the club cannot serve from the chosen start render disabled rather than
  // selectable-then-dead-ended. Same gate the mobile sheet applies; the dropdown used
  // to have none, which is why changing the length used to clear the selection here.
  // Undefined (no start picked yet, or a caller that does not compute it) = all enabled.
  const isDurationDisabled = (duration: number) =>
    availableDurations !== undefined && !availableDurations.includes(duration);
  // Stacked layout — labeled, full-width controls for the mobile filter sheet.
  if (layout === 'stacked') {
    return (
      <div
        className="flex flex-col gap-5"
        role="group"
        aria-label="Time slot filters"
      >
        {/* Duration — static label when the length is club-fixed (demo flow) */}
        {durationDisplay === 'label' && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-fg-secondary">Duration</span>
            <span
              className="flex items-center gap-2 text-sm font-semibold text-fg-secondary"
              data-testid="booking-duration-label"
            >
              <HourglassIcon className="size-[18px] shrink-0 text-fg-primary" />
              {formatDuration(selectedDuration)}
            </span>
          </div>
        )}

        {/* Duration selector */}
        {durationDisplay === 'select' && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-fg-secondary">Duration</span>
          <Select
            value={String(selectedDuration)}
            onValueChange={(value) => onDurationChange(Number(value))}
          >
            <SelectTrigger
              className="w-full"
              aria-label="Select duration"
              data-testid="booking-duration-select"
            >
              {/* !flex beats the trigger's [&>span]:line-clamp-1, which would
                  otherwise force display:-webkit-box and stack icon over text */}
              <span className="!flex min-w-0 items-center gap-2">
                <HourglassIcon className="size-[18px] shrink-0 text-fg-primary" />
                {formatTriggerLabel ? <SelectValue>{formatTriggerLabel(selectedDuration)}</SelectValue> : <SelectValue />}
              </span>
            </SelectTrigger>
            <SelectContent>
              {durationOptions.map((duration) => (
                <SelectItem
                  key={duration}
                  value={String(duration)}
                  disabled={isDurationDisabled(duration)}
                  data-testid={`booking-duration-option-${duration}`}
                >
                  {formatOptionLabel(duration)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}

        {/* Bay selector */}
        {showBayFilter && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-fg-secondary">Bay</span>
            <Select
              value={String(selectedBayId)}
              onValueChange={(value) =>
                onBayChange(value === 'all' ? 'all' : Number(value))
              }
            >
              <SelectTrigger
                className="w-full"
                aria-label="Select bay"
                data-testid="booking-bay-select"
              >
                <span className="!flex min-w-0 items-center gap-2">
                  <LocationIcon className="size-5 shrink-0 text-fg-primary" />
                  <SelectValue />
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bays</SelectItem>
                {bays.map((bay) => (
                  <SelectItem
                    key={bay.id}
                    value={String(bay.id)}
                    disabled={!bay.isActive}
                    data-testid={`booking-bay-option-${bay.name}`}
                  >
                    {bay.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex flex-row items-center gap-3"
      role="group"
      aria-label="Time slot filters"
    >
      {/* Duration — static label when the length is club-fixed (demo flow) */}
      {durationDisplay === 'label' && (
        <div className="flex items-center gap-2" data-testid="booking-duration-label">
          <HourglassIcon className="size-[18px] text-fg-primary" />
          <span className="pl-1 pr-1 text-sm font-semibold text-fg-secondary">
            {formatDuration(selectedDuration)}
          </span>
        </div>
      )}

      {/* Duration selector */}
      {durationDisplay === 'select' && (
      <div className="flex items-center gap-2">
        <HourglassIcon className="size-[18px] text-fg-primary" />
        <Select
          value={String(selectedDuration)}
          onValueChange={(value) => onDurationChange(Number(value))}
        >
          <SelectTrigger
            className={TRIGGER_CLASS_NAMES}
            aria-label="Select duration"
            data-testid="booking-duration-select"
          >
            {formatTriggerLabel ? <SelectValue>{formatTriggerLabel(selectedDuration)}</SelectValue> : <SelectValue />}
          </SelectTrigger>
          <SelectContent>
            {durationOptions.map((duration) => (
              <SelectItem
                key={duration}
                value={String(duration)}
                disabled={isDurationDisabled(duration)}
                data-testid={`booking-duration-option-${duration}`}
              >
                {formatOptionLabel(duration)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      )}

      {/* Bay selector */}
      {showBayFilter && (
        <div className="flex items-center gap-2">
          <LocationIcon className="size-5 text-fg-primary" />
          <Select
            value={String(selectedBayId)}
            onValueChange={(value) =>
              onBayChange(value === 'all' ? 'all' : Number(value))
            }
          >
            <SelectTrigger
              className={TRIGGER_CLASS_NAMES}
              aria-label="Select bay"
              data-testid="booking-bay-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bays</SelectItem>
              {bays.map((bay) => (
                <SelectItem
                  key={bay.id}
                  value={String(bay.id)}
                  disabled={!bay.isActive}
                  data-testid={`booking-bay-option-${bay.name}`}
                >
                  {bay.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
